import { create } from 'zustand';
import {
  Currency,
  Field,
  Pool,
  Rounding,
  TickMath,
  tickToPrice
} from '@cryptoalgebra/kim-sdk';
import {
  FullRange,
  Mode,
  StrategyProvider,
  useMintState
} from '@app/state/mintStore';
import { PresetsType } from '@app/types/presets';
import { useCallback, useMemo } from 'react';

interface LiquidityWizardState {
  readonly step: number;
  readonly currencyA: Currency | undefined;
  readonly currencyB: Currency | undefined;
  readonly mode: Mode;
  readonly flow: string;
  readonly provider: StrategyProvider | null;
  readonly strategy: string | null;
  readonly tokenId: string;
  readonly chainId: number | undefined;

  readonly startPriceTypedValue: string; // for the case when there's no liquidity
  readonly independentField: Field;
  readonly typedValue: string;
  readonly leftRangeTypedValue: string | FullRange;
  readonly rightRangeTypedValue: string | FullRange;
  readonly preset: PresetsType | null;
  readonly initialTokenPrice: string;

  actions: {
    setStep: (nextStep: number) => void;
    setCurrencyA: (currency: Currency | undefined) => void;
    setCurrencyB: (currency: Currency | undefined) => void;
    setMode: (nextMode: Mode) => void;
    setProvider: (provider: StrategyProvider | null) => void;
    setStrategy: (strategy: string | null) => void;
    setTokenId: (tokenId: string) => void;
    setFlow: (flow: string) => void;
    setChainId: (chainId: number | undefined) => void;
    reset: () => void;

    typeStartPriceInput: (typedValue: string) => void;
    typeLeftRangeInput: (typedValue: string) => void;
    typeRightRangeInput: (typedValue: string) => void;
    setFullRange: () => void;
    updateSelectedPreset: (preset: PresetsType | null) => void;
    typeInput: (field: Field, typedValue: string, noLiquidity: boolean) => void;
    setInitialTokenPrice: (typedValue: string) => void;
  };
}

export const useLiquidityWizardState = create<LiquidityWizardState>(
  (setState, getState) => ({
    step: 0,
    currencyA: undefined,
    currencyB: undefined,
    mode: 'manual',
    provider: null,
    strategy: null,
    tokenId: '',
    flow: 'v4',
    chainId: undefined,

    startPriceTypedValue: '',
    leftRangeTypedValue: '',
    rightRangeTypedValue: '',
    independentField: Field.CURRENCY_A,
    typedValue: '',
    preset: null,
    initialTokenPrice: '',

    actions: {
      setStep: (step: number) => setState({ step }),
      setCurrencyA: (currency: Currency | undefined) =>
        setState({ currencyA: currency }),
      setCurrencyB: (currency: Currency | undefined) =>
        setState({ currencyB: currency }),
      setMode: (nextMode: Mode) => setState({ mode: nextMode }),
      setProvider: (provider: StrategyProvider | null) =>
        setState({ provider }),
      setStrategy: (strategy: string | null) => setState({ strategy }),
      setTokenId: (tokenId: string) => setState({ tokenId }),
      setFlow: (flow: string) => setState({ flow }),
      setChainId: (chainId: number | undefined) => setState({ chainId }),
      reset: () =>
        setState({
          step: 0,
          currencyA: undefined,
          currencyB: undefined,
          mode: 'manual',
          provider: null,
          strategy: null,
          tokenId: '',
          flow: 'v4'
        }),

      typeStartPriceInput: (typedValue: string) =>
        setState({ startPriceTypedValue: typedValue }),
      typeLeftRangeInput: (typedValue: string) =>
        setState({ leftRangeTypedValue: typedValue }),
      typeRightRangeInput: (typedValue: string) =>
        setState({ rightRangeTypedValue: typedValue }),
      typeInput: (field: Field, typedValue: string, noLiquidity: boolean) => {
        if (noLiquidity) {
          if (field === getState().independentField) {
            setState({ independentField: field, typedValue });
          } else {
            setState({ independentField: field, typedValue });
          }
        } else {
          setState({ independentField: field, typedValue });
        }
      },
      setFullRange: () =>
        setState({ leftRangeTypedValue: true, rightRangeTypedValue: true }),
      updateSelectedPreset: (preset: PresetsType | null) =>
        setState({ preset }),
      setInitialTokenPrice: (typedValue: string) =>
        setState({ initialTokenPrice: typedValue })
    }
  })
);

export function useRangeHopCallbacks(
  baseCurrency: Currency | undefined,
  quoteCurrency: Currency | undefined,
  tickSpacing: number,
  tickLower: number | undefined,
  tickUpper: number | undefined,
  pool?: Pool | undefined | null
) {
  const {
    actions: { setFullRange }
  } = useLiquidityWizardState();

  const baseToken = useMemo(() => baseCurrency?.wrapped, [baseCurrency]);
  const quoteToken = useMemo(() => quoteCurrency?.wrapped, [quoteCurrency]);

  const getDecrementLower = useCallback(
    (rate = 1) => {
      if (
        baseToken &&
        quoteToken &&
        typeof tickLower === 'number' &&
        tickSpacing
      ) {
        const newPrice = tickToPrice(
          baseToken,
          quoteToken,
          tickLower - tickSpacing * rate
        );

        return newPrice.toSignificant(5, undefined, Rounding.ROUND_UP);
      }
      // use pool current tick as starting tick if we have pool but no tick input

      if (
        !(typeof tickLower === 'number') &&
        baseToken &&
        quoteToken &&
        tickSpacing &&
        pool
      ) {
        const newPrice = tickToPrice(
          baseToken,
          quoteToken,
          pool.tickCurrent - tickSpacing * rate
        );

        return newPrice.toSignificant(5, undefined, Rounding.ROUND_UP);
      }

      return '';
    },
    [baseToken, quoteToken, tickLower, tickSpacing, pool]
  );

  const getIncrementLower = useCallback(
    (rate = 1) => {
      if (
        baseToken &&
        quoteToken &&
        typeof tickLower === 'number' &&
        tickSpacing
      ) {
        const newPrice = tickToPrice(
          baseToken,
          quoteToken,
          tickLower + tickSpacing * rate
        );

        return newPrice.toSignificant(5, undefined, Rounding.ROUND_UP);
      }

      // use pool current tick as starting tick if we have pool but no tick input
      if (
        !(typeof tickLower === 'number') &&
        baseToken &&
        quoteToken &&
        tickSpacing &&
        pool
      ) {
        const newPrice = tickToPrice(
          baseToken,
          quoteToken,
          pool.tickCurrent + tickSpacing * rate
        );

        return newPrice.toSignificant(5, undefined, Rounding.ROUND_UP);
      }

      return '';
    },
    [baseToken, quoteToken, tickLower, tickSpacing, pool]
  );

  const getDecrementUpper = useCallback(
    (rate = 1) => {
      if (
        baseToken &&
        quoteToken &&
        typeof tickUpper === 'number' &&
        tickSpacing
      ) {
        const newPrice = tickToPrice(
          baseToken,
          quoteToken,
          tickUpper - tickSpacing * rate
        );

        return newPrice.toSignificant(5, undefined, Rounding.ROUND_UP);
      }

      // use pool current tick as starting tick if we have pool but no tick input
      if (
        !(typeof tickUpper === 'number') &&
        baseToken &&
        quoteToken &&
        tickSpacing &&
        pool
      ) {
        const newPrice = tickToPrice(
          baseToken,
          quoteToken,
          pool.tickCurrent - tickSpacing * rate
        );

        return newPrice.toSignificant(5, undefined, Rounding.ROUND_UP);
      }

      return '';
    },
    [baseToken, quoteToken, tickUpper, tickSpacing, pool]
  );

  const getIncrementUpper = useCallback(
    (rate = 1) => {
      if (
        baseToken &&
        quoteToken &&
        typeof tickUpper === 'number' &&
        tickSpacing
      ) {
        const newPrice = tickToPrice(
          baseToken,
          quoteToken,
          tickUpper + tickSpacing * rate
        );

        return newPrice.toSignificant(5, undefined, Rounding.ROUND_UP);
      }

      // use pool current tick as starting tick if we have pool but no tick input
      if (
        !(typeof tickUpper === 'number') &&
        baseToken &&
        quoteToken &&
        tickSpacing &&
        pool
      ) {
        const newPrice = tickToPrice(
          baseToken,
          quoteToken,
          pool.tickCurrent + tickSpacing * rate
        );

        return newPrice.toSignificant(5, undefined, Rounding.ROUND_UP);
      }

      return '';
    },
    [baseToken, quoteToken, tickUpper, tickSpacing, pool]
  );

  const getSetRange = useCallback(
    (numTicks: number) => {
      if (baseToken && quoteToken && tickSpacing && pool) {
        // calculate range around current price given `numTicks`
        const newPriceLower = tickToPrice(
          baseToken,
          quoteToken,
          Math.max(TickMath.MIN_TICK, pool.tickCurrent - numTicks)
        );
        const newPriceUpper = tickToPrice(
          baseToken,
          quoteToken,
          Math.min(TickMath.MAX_TICK, pool.tickCurrent + numTicks)
        );

        return [
          newPriceLower.toSignificant(5, undefined, Rounding.ROUND_UP),
          newPriceUpper.toSignificant(5, undefined, Rounding.ROUND_UP)
        ];
      }

      return ['', ''];
    },
    [baseToken, quoteToken, tickSpacing, pool]
  );

  const getSetFullRange = useCallback(() => setFullRange(), [setFullRange]);

  return {
    getDecrementLower,
    getIncrementLower,
    getDecrementUpper,
    getIncrementUpper,
    getSetRange,
    getSetFullRange,
    setFullRange
  };
}

export function useMintActionHandlers(noLiquidity: boolean | undefined): {
  onFieldAInput: (typedValue: string) => void;
  onFieldBInput: (typedValue: string) => void;
  onLeftRangeInput: (typedValue: string) => void;
  onRightRangeInput: (typedValue: string) => void;
  onStartPriceInput: (typedValue: string) => void;
} {
  const {
    actions: {
      typeInput,
      typeLeftRangeInput,
      typeRightRangeInput,
      typeStartPriceInput
    }
  } = useLiquidityWizardState();

  const onFieldAInput = useCallback(
    (typedValue: string) =>
      typeInput(Field.CURRENCY_A, typedValue, noLiquidity === true),
    [noLiquidity, typeInput]
  );

  const onFieldBInput = useCallback(
    (typedValue: string) =>
      typeInput(Field.CURRENCY_B, typedValue, noLiquidity === true),
    [noLiquidity, typeInput]
  );

  const onLeftRangeInput = useCallback(
    (typedValue: string) => typeLeftRangeInput(typedValue),
    [typeLeftRangeInput]
  );

  const onRightRangeInput = useCallback(
    (typedValue: string) => typeRightRangeInput(typedValue),
    [typeRightRangeInput]
  );

  const onStartPriceInput = useCallback(
    (typedValue: string) => typeStartPriceInput(typedValue),
    [typeStartPriceInput]
  );

  return {
    onFieldAInput,
    onFieldBInput,
    onLeftRangeInput,
    onRightRangeInput,
    onStartPriceInput
  };
}
